import { isWeb } from "@memorang/helpers/platform";
import type { WordLimit } from "@memorang/types";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import Box from "../../../Box";

const getChipState = ({
	wordCount,
	wordLimit,
}: {
	wordCount: number;
	wordLimit?: WordLimit;
}) => {
	const isAboveMax = wordLimit?.max != null && wordCount > wordLimit.max;
	const hasLimit = wordLimit?.min != null || wordLimit?.max != null;
	if (isAboveMax) {
		return "error";
	}
	if (hasLimit) {
		return "constrained";
	}
	return "default";
};

export const WordCountChip = ({
	wordCount,
	wordLimit,
	pluralWord,
	overrideText,
}: {
	wordCount: number;
	wordLimit?: WordLimit;
	pluralWord: string;
	overrideText?: string;
}) => {
	const theme = useAppTheme();
	const chipState = getChipState({ wordCount, wordLimit });
	const hasConstraint = chipState !== "default";
	const isError = chipState === "error";
	const { isMobile } = useDevice();

	const containerProps = {
		borderWidth: 1,
		borderColor: isError ? theme.colors.error : theme.colors.outlineVariant,
		borderRadius: theme.roundness * 2,
		paddingHorizontal: isMobile ? 6 : 8,
		paddingVertical: isMobile ? 3 : 4,
		backgroundColor: hasConstraint
			? theme.colors.background
			: theme.colors.surface,
	} as const;

	const boldColor = isError ? theme.colors.error : theme.colors.onSurface;
	const secondaryColor = isError
		? theme.colors.error
		: theme.colors.onSurfaceVariant;

	const textVariant = isWeb ? "bodyMedium" : "bodySmall";
	const lineHeight = isWeb ? 20 : 16;

	if (overrideText) {
		return (
			<Box {...containerProps}>
				<Text
					variant={textVariant}
					style={{ color: boldColor, fontWeight: "500" }}
				>
					{overrideText}
				</Text>
			</Box>
		);
	}

	return (
		<Box {...containerProps}>
			<Box flexDirection="row" alignItems="center" gap={2}>
				<Text
					variant={textVariant}
					style={{ color: boldColor, fontWeight: "500", lineHeight }}
				>
					{wordCount}
				</Text>
				{wordLimit?.max != null ? (
					<>
						<Text
							variant={textVariant}
							style={{ color: secondaryColor, fontWeight: "400", lineHeight }}
						>
							/
						</Text>
						<Text
							variant={textVariant}
							style={{ color: secondaryColor, fontWeight: "400", lineHeight }}
						>
							{wordLimit.max}
						</Text>
					</>
				) : null}
				<Text
					variant={textVariant}
					style={{ color: boldColor, fontWeight: "500", lineHeight }}
				>
					{pluralWord}
				</Text>
			</Box>
		</Box>
	);
};
